import React, { useState, useEffect, useMemo } from 'react';
import { charactersAPI } from '../../services/api';
import './CharacterCreationModal.css';

function CharacterCreationModal({ isOpen, onClose, campaignData, onCharacterCreated }) {
  const [name, setName] = useState('');
  const [characterType, setCharacterType] = useState('Intolerant');

  // Standalone creation uses a user-adjustable XP pool. Campaign creation must use campaignData.startingXp.
  const [customStartingXp, setCustomStartingXp] = useState(1000);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Extract campaign data if provided, otherwise use defaults
  const campaignId = campaignData?.campaignId || null;
  const campaignName = campaignData?.campaignName || '';
  const isStandaloneCreation = !campaignData;

  // --- IMPORTANT FIXES ---
  // 1) Do not use `||` for startingXp (0 would incorrectly fallback).
  // 2) When campaignData is present, always use its startingXp (authoritative).
  // 3) Reset customStartingXp only for standalone creation; don't overwrite campaign data on open.
  const startingXp = useMemo(() => {
    if (!isStandaloneCreation) return (campaignData?.startingXp ?? 1000);
    return (customStartingXp ?? 1000);
  }, [isStandaloneCreation, campaignData?.startingXp, customStartingXp]);

  // Calculate XP costs based on character type
  const getTypeCost = (type) => {
    switch (type) {
      case 'Ascended': return 500;
      case 'Tolerant': return 300;
      case 'Intolerant': return 0;
      default: return 0;
    }
  };

  const availableXp = Math.max(0, startingXp - getTypeCost(characterType));

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName('');
      setCharacterType('Intolerant');

      // Only reset custom starting XP for standalone creation.
      // For campaign-based creation, startingXp must come from campaignData.
      if (isStandaloneCreation) {
        setCustomStartingXp(1000);
      }

      setError(null);
    }
  }, [isOpen, isStandaloneCreation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Calculate actual starting XP after character type cost
      const typeCost = getTypeCost(characterType);
      const actualStartingXP = Math.max(0, startingXp - typeCost);

      const characterData = {
        name: name.trim(),
        characterType,
        campaignId, // null if standalone creation

        // IMPORTANT: We continue using `xp` because your backend currently expects it.
        // If/when you transition fully to totalXp/availableXp, adjust here accordingly.
        xp: actualStartingXP,

        level: 1,
        physique: 1,
        coordination: 1,
        logic: 1,
        perception: 1,
        presence: 1,
        resilience: 1,
        feats: {},
        forges: {},
        equipment: {}
      };

      const res = await charactersAPI.createCharacter(characterData);

      // Notify parent component
      onCharacterCreated?.(res.data.character);

      // Close modal
      onClose?.();

      // Reset form
      setName('');
      setCharacterType('Intolerant');
    } catch (err) {
      console.error('Failed to create character:', err);
      setError(err?.response?.data?.error || 'Failed to create character');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="modal-container character-creation-modal">
        <div className="modal-header character-editor-header">
          <h3>
            {isStandaloneCreation
              ? 'Create New Character'
              : `Create Character for ${campaignName}`
            }
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {isStandaloneCreation && (
            <div className="form-field">
              <label htmlFor="startingXp">Starting XP Pool</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <input
                  id="startingXp"
                  type="number"
                  min="100"
                  max="10000"
                  step="100"
                  value={customStartingXp}
                  onChange={(e) => setCustomStartingXp(parseInt(e.target.value, 10) || 1000)}
                  disabled={isSubmitting}
                  style={{ flex: 1 }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setCustomStartingXp(1000)}
                    disabled={isSubmitting}
                    style={{ padding: '8px 12px', fontSize: '0.9rem' }}
                  >
                    Default (1000)
                  </button>
                </div>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#9d4edd', marginTop: '8px' }}>
                Set the starting XP for your character. Default is 1000 XP. Can be adjusted for different power levels.
              </p>
            </div>
          )}

          <div className="campaign-info-box">
            <p className="info-text">
              <strong>{isStandaloneCreation ? 'Total XP Pool:' : 'Campaign Starting XP:'}</strong> {startingXp} XP
            </p>
            {isStandaloneCreation && (
              <p className="info-text hint" style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                This character can be used in any campaign that accepts characters of this type.
              </p>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="characterName">Character Name *</label>
            <input
              id="characterName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter character name"
              required
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="form-field">
            <label>Character Type *</label>
            <div className="character-type-grid">
              <div
                className={`type-card ${characterType === 'Intolerant' ? 'selected' : ''}`}
                onClick={() => !isSubmitting && setCharacterType('Intolerant')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (!isSubmitting && (e.key === 'Enter' || e.key === ' ')) setCharacterType('Intolerant'); }}
              >
                <input
                  type="radio"
                  name="characterType"
                  value="Intolerant"
                  checked={characterType === 'Intolerant'}
                  onChange={(e) => setCharacterType(e.target.value)}
                  disabled={isSubmitting}
                />
                <div className="type-content">
                  <h4>Intolerant</h4>
                  <p className="type-cost">Cost: 0 XP</p>
                  <p className="type-available">Available: {startingXp} XP</p>
                  <p className="type-desc">No Aetherion abilities. Full XP for stats, skills, and training.</p>
                </div>
              </div>

              <div
                className={`type-card ${characterType === 'Tolerant' ? 'selected' : ''}`}
                onClick={() => !isSubmitting && setCharacterType('Tolerant')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (!isSubmitting && (e.key === 'Enter' || e.key === ' ')) setCharacterType('Tolerant'); }}
              >
                <input
                  type="radio"
                  name="characterType"
                  value="Tolerant"
                  checked={characterType === 'Tolerant'}
                  onChange={(e) => setCharacterType(e.target.value)}
                  disabled={isSubmitting}
                />
                <div className="type-content">
                  <h4>Tolerant</h4>
                  <p className="type-cost">Cost: 300 XP</p>
                  <p className="type-available">Available: {Math.max(0, startingXp - 300)} XP</p>
                  <p className="type-desc">Can use Aetherion with toxicity penalties. Balanced capabilities.</p>
                </div>
              </div>

              <div
                className={`type-card ${characterType === 'Ascended' ? 'selected' : ''}`}
                onClick={() => !isSubmitting && setCharacterType('Ascended')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (!isSubmitting && (e.key === 'Enter' || e.key === ' ')) setCharacterType('Ascended'); }}
              >
                <input
                  type="radio"
                  name="characterType"
                  value="Ascended"
                  checked={characterType === 'Ascended'}
                  onChange={(e) => setCharacterType(e.target.value)}
                  disabled={isSubmitting}
                />
                <div className="type-content">
                  <h4>Ascended</h4>
                  <p className="type-cost">Cost: 500 XP</p>
                  <p className="type-available">Available: {Math.max(0, startingXp - 500)} XP</p>
                  <p className="type-desc">Natural Aetherion powers with regenerating charges. Supernatural abilities.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="xp-summary">
            <div className="summary-row">
              <span>Starting XP:</span>
              <span>{startingXp}</span>
            </div>
            <div className="summary-row">
              <span>Character Type Cost:</span>
              <span>-{getTypeCost(characterType)}</span>
            </div>
            <div className="summary-row total">
              <span>Available for Development:</span>
              <span>{availableXp} XP</span>
            </div>
          </div>

          <p className="note">
            <strong>Note:</strong> Your character will start with {availableXp} XP available to spend on stats, skills, and abilities in the character editor.
          </p>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create Character'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CharacterCreationModal;
